/* Mapa dos EventType (ep33_event_list) + badges no tema do painel. */
import type { EventSummary } from '@/shared/services/eventManagerService';

export interface EventTypeDef { label: string; description: string; editor?: 'shop' | 'monster-drop' | 'monster-attack' }

export const EVENT_TYPES: Record<number, EventTypeDef> = {
  1:  { label: 'Treasure', description: 'Daily treasure event' },
  2:  { label: 'Item Collection', description: 'NPC Event Shop / troca de itens', editor: 'shop' },
  3:  { label: 'Dungeon Clear', description: 'Limpar dungeon' },
  4:  { label: 'Combo', description: 'Atingir X de combo' },
  5:  { label: 'Kill Count', description: 'Matar X monstros' },
  6:  { label: 'Time Play', description: 'Ficar online X minutos' },
  7:  { label: 'Level Up', description: 'Subir de nível' },
  8:  { label: 'Bingo', description: 'Evento de bingo' },
  9:  { label: 'Random Box', description: 'Caixa aleatória' },
  10: { label: 'Random Box V2', description: 'Caixa aleatória v2' },
  11: { label: 'Monster Attack', description: 'Invasão de monstro agendada', editor: 'monster-attack' },
  12: { label: 'Level Up Mail', description: 'Envia mail ao subir de nível' },
  13: { label: 'Enchant', description: 'Upgrade de item' },
  14: { label: 'Monster Drop', description: 'Drop extra por dungeon / mob', editor: 'monster-drop' },
  15: { label: 'Quest Reward', description: 'Completar quest' },
  16: { label: 'NPC Talk', description: 'Falar com NPC' },
  17: { label: 'Use Item', description: 'Usar item específico' },
  18: { label: 'Join Party', description: 'Entrar em party' },
  19: { label: 'Complete Mission', description: 'Terminar missão' },
  20: { label: 'Dungeon Enter', description: 'Entrar em dungeon' },
  21: { label: 'Point Accumulation', description: 'Acumular pontos' },
  22: { label: 'Special Craft', description: 'Craft especial' },
  23: { label: 'Mission Clear Time', description: 'Limpar dungeon em X seg' },
  24: { label: 'PvP Win', description: 'Vencer PvP' },
  25: { label: 'Achievement', description: 'Conquista' },
  26: { label: 'Guild Join', description: 'Entrar em guild' },
  27: { label: 'Guild Mission', description: 'Missão de guild' },
  28: { label: 'Battle Style Change', description: 'Troca de classe' },
  29: { label: 'Mission Score Rank', description: 'Ranking por score' },
  30: { label: 'ForceGem Spend', description: 'Gastar Force Gem' },
  31: { label: 'Auto Mission', description: 'Missão automática' },
  32: { label: 'Time Ranking', description: 'Ranking por tempo' },
  33: { label: 'War Participation', description: 'Participar da Nation War' },
  34: { label: 'Dungeon Complete Ex', description: 'Clear estendido' },
  35: { label: 'Custom Func Call', description: 'Chama função custom' },
};

export const eventTypeDef = (t: number): EventTypeDef =>
  EVENT_TYPES[t] ?? { label: `Tipo ${t}`, description: 'Tipo não catalogado' };

/* filtros de tipo pra dropdown (só os que existem em uso comum + suportados no topo) */
export const TYPE_FILTER_OPTIONS = [2, 11, 14, 1, 9, 10, 8, 12, 13].map(t => ({ value: t, label: eventTypeDef(t).label }));

const TYPE_COLORS: Record<number, string> = {
  2:  'bg-[#00e1d7]/12 text-[#00e1d7] border-[#00e1d7]/30',
  11: 'bg-rose-500/12 text-rose-300 border-rose-500/30',
  14: 'bg-violet-500/12 text-violet-300 border-violet-500/30',
};

export function EventTypeBadge({ type }: { type: number }) {
  const def = eventTypeDef(type);
  const cls = TYPE_COLORS[type] ?? 'bg-white/[0.06] text-white/60 border-white/10';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold
                      uppercase tracking-wide border ${cls}`}
          title={def.description}>
      {def.label}
      {!def.editor && <span className="opacity-50 normal-case font-normal">· sem editor</span>}
    </span>
  );
}

const STATUS_META: Record<EventSummary['status'], { label: string; cls: string }> = {
  active:    { label: 'Ativo',    cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  inactive:  { label: 'Inativo',  cls: 'bg-white/[0.06] text-white/55 border-white/10' },
  scheduled: { label: 'Agendado', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  expired:   { label: 'Expirado', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

export function EventStatusBadge({ status }: { status: EventSummary['status'] }) {
  const m = STATUS_META[status] ?? STATUS_META.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px]
                      font-bold border ${m.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {m.label}
    </span>
  );
}

export const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
};
export const fmtDateTime = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};
/* datetime-local <-> ISO (o input datetime-local nao carrega timezone) */
export const toLocalInput = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
export const fromLocalInput = (v: string) => (v ? v : '');
